import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useTestSend } from "@/hooks/sms/useTestSend";
import { formatForDisplay, normalizeToNational10 } from "@/utils/smsShared";

interface TestSendCardProps {
  messageBody: string;
  promotionId: string | null;
  imageUrl: string | null;
  expirationDays: number | null;
  timerMinutes: number | null;
  /** The store's saved test number: the prefill, and the fallback recipient
   *  while the deployed sms-actions still ignores a typed number. */
  ownerTestPhone: string | null;
  disabled?: boolean;
}

/** "Test before you send": texts the real message, with a real working offer
 *  link, to any phone the owner types. Backend falls back to the saved
 *  owner_test_phone if the deployed function predates typed recipients, so
 *  the card always reports which number was actually texted. */
export function TestSendCard({
  messageBody,
  promotionId,
  imageUrl,
  expirationDays,
  timerMinutes,
  ownerTestPhone,
  disabled,
}: TestSendCardProps) {
  const [phone, setPhone] = useState(ownerTestPhone ?? "");
  const testSend = useTestSend();
  const { toast } = useToast();

  useEffect(() => {
    // Prefill once settings load; never clobber a number the owner typed.
    setPhone((current) => (current === "" && ownerTestPhone ? ownerTestPhone : current));
  }, [ownerTestPhone]);

  const normalized = normalizeToNational10(phone);
  const phoneInvalid = phone.trim() !== "" && !normalized;
  const typedDiffersFromSaved = !!normalized && !!ownerTestPhone && normalized !== ownerTestPhone;

  const send = () => {
    testSend.mutate(
      {
        to: phone,
        messageBody,
        promotionId,
        imageUrl,
        expirationDays,
        timerMinutes,
      },
      {
        onSuccess: (outcome) => {
          if (outcome.sentTo) {
            toast({ title: "Test sent", description: `Sent to ${formatForDisplay(outcome.sentTo)}.` });
          } else {
            toast({
              title: "Test sent to your saved number",
              description: `The updated test sending isn't live yet, so it went to ${
                ownerTestPhone ? formatForDisplay(ownerTestPhone) : "your saved test number"
              } instead of the number you typed.`,
            });
          }
        },
        onError: (error) => {
          toast({ title: "Test failed", description: error.message, variant: "destructive" });
        },
      },
    );
  };

  return (
    <Card>
      <CardContent className="space-y-2 p-4 sm:p-6">
        <Label htmlFor="test-send-phone" className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
          Test before you send
        </Label>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            id="test-send-phone"
            type="tel"
            inputMode="tel"
            className="h-11 flex-1 basis-40 text-base"
            placeholder="(209) 555-0100"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            aria-invalid={phoneInvalid}
          />
          <Button
            type="button"
            variant="outline"
            className="h-11"
            disabled={disabled || testSend.isPending || phoneInvalid || phone.trim() === "" || messageBody.trim() === ""}
            onClick={send}
          >
            {testSend.isPending ? "Sending…" : "Send test to this number"}
          </Button>
        </div>
        {phoneInvalid && (
          <p className="text-xs text-status-danger">Use a full US mobile number, like (209) 555-0100.</p>
        )}
        <p className="text-xs leading-relaxed text-muted-foreground">
          Sends the real text, with a real working link, to the phone you type. Doesn't count against caps.
        </p>
        {typedDiffersFromSaved && (
          <p className="text-xs leading-relaxed text-muted-foreground">
            If the updated test sending isn't live yet, the test goes to your saved number
            {ownerTestPhone ? ` (${formatForDisplay(ownerTestPhone)})` : ""} instead; the confirmation will say which.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
